import React, { useEffect, useState } from "react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { PageTitle, StatCard, Table, Td, fmtDate } from "../../components/admin/AdminUi";
import { inr } from "../../components/admin/pricing/PricingUi";
import { api } from "../../lib/api";

const BADGE = { paid: "text-emerald-700 border-emerald-200 bg-emerald-50", created: "text-slate-500 border-slate-300 bg-slate-100", processing: "text-amber-600 border-amber-200 bg-amber-50" };
const FILTERS = [["all", "All"], ["paid", "Paid"], ["created", "Not completed"]];

const MonthsChart = ({ months }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="earnings-chart">
    <h3 className="text-sm font-semibold text-slate-900 mb-4">Revenue & profit by month</h3>
    {months.length === 0 ? <p className="text-sm text-slate-500">No paid orders yet.</p> : (
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={months}>
            <XAxis dataKey="month" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => inr(v)} contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, fontSize: 12 }} />
            <Bar dataKey="revenue_inr" name="Revenue" fill="#38BDF8" radius={[6, 6, 0, 0]} />
            <Bar dataKey="profit_inr" name="Profit" fill="#10B981" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    )}
  </div>
);

export default function AdminOrders() {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.adminEarnings().then(setD).catch(() => setD({ totals: {}, months: [], orders: [] })); }, []);
  if (!d) return <div className="grid sm:grid-cols-4 gap-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-28 rounded-2xl shimmer" />)}</div>;
  const t = d.totals;
  const rows = d.orders.filter((o) => filter === "all" || (filter === "paid" ? o.status === "paid" : o.status !== "paid"));
  return (
    <div data-testid="admin-orders" className="space-y-6">
      <PageTitle title="Payments & earnings" desc="Every credit purchase, what it cost you on Emergent and what you earned." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Revenue" value={inr(t.revenue_inr)} sub={`${t.orders || 0} paid orders`} testid="earnings-revenue" />
        <StatCard label="Your Emergent cost" value={inr(t.cost_inr)} sub={`${(t.credits_sold || 0).toLocaleString("en-IN")} credits sold`} testid="earnings-cost" />
        <StatCard label="Profit" value={inr(t.profit_inr)} accent="text-emerald-600" sub={t.revenue_inr ? `${Math.round((t.profit_inr / t.revenue_inr) * 100)}% margin` : "—"} testid="earnings-profit" />
        <StatCard label="Credits used" value={(d.credits_used_all_time || 0).toLocaleString("en-IN")} sub="by all users' builds" testid="earnings-credits-used" />
      </div>
      <MonthsChart months={d.months} />
      <div className="flex gap-2" data-testid="orders-filter">
        {FILTERS.map(([id, l]) => (
          <button key={id} onClick={() => setFilter(id)} data-testid={`orders-filter-${id}`}
            className={`h-9 px-4 rounded-full text-sm transition-colors ${filter === id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
        ))}
      </div>
      <Table head={["User", "Pack", "Credits", "Paid", "Your cost", "Profit", "Status", "Razorpay", "Date"]} testid="orders-table" empty={rows.length === 0 && "No orders here yet."}>
        {rows.map((o) => (
          <tr key={o.id} data-testid={`order-row-${o.id}`} className="hover:bg-slate-50">
            <Td className="text-slate-600">{o.user_email}</Td>
            <Td>{o.pack_name || o.pack_id}</Td>
            <Td className="font-mono">{o.credits}</Td>
            <Td className="font-mono text-slate-900">{inr(o.price_inr)}</Td>
            <Td className="font-mono">{inr(o.cost_inr)}</Td>
            <Td className={`font-mono ${o.profit_inr < 0 ? "text-rose-600" : "text-emerald-700"}`}>{inr(o.profit_inr)}</Td>
            <Td><span className={`px-1.5 py-0.5 rounded border text-[10px] font-mono ${BADGE[o.status] || BADGE.created}`}>{o.status}</span></Td>
            <Td className="text-[11px] font-mono text-slate-500">{o.source === "manual" ? `Direct · ${o.reference || o.method}` : o.razorpay_payment_id || o.razorpay_order_id}</Td>
            <Td className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(o.created_at)}</Td>
          </tr>
        ))}
      </Table>
    </div>
  );
}
